import React, { useId, useState } from 'react';
import { ErrorMessage, Field, useFormikContext } from 'formik';
import { InputTextarea } from 'primereact/inputtextarea';
import { useTranslation } from 'localization';
import TextEditorAtom from '../../atoms/TextEditor/TextEditorAtom';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import './SphereDescriptionEditor.scss';

const DESCRIPTION_FIELD = 'properties.description';

/** The preview and the rich editor share one height, so opening the editor does not move the page. */
const EDITOR_HEIGHT = '200px';

/**
 * The description's text. The stored value is HTML a person wrote, so it is parsed in an inert
 * document: nothing in it loads or runs, unlike markup assigned to an element of the live page.
 */
export const getPlainText = (html?: string): string => {
  if (!html) return '';
  return new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';
};

interface SphereDescriptionEditorProps {
  editable?: boolean;
}

/**
 * The sphere description: a plain textarea on mobile, and on desktop a preview that opens the rich
 * text editor on focus. Reads and writes `properties.description` through the surrounding Formik form.
 */
const SphereDescriptionEditor: React.FC<SphereDescriptionEditorProps> = ({ editable }) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [isEditing, setIsEditing] = useState(false);
  const placeholder = t('sphere.identity.descriptionPlaceholder');
  // Unique per instance, so two open editors never share a label target.
  const inputId = `sphere-description-${useId()}`;
  const labelId = `${inputId}-label`;

  return (
    <div className="form-group sphere-description-editor" data-testid="sphere-description-editor">
      <label id={labelId} htmlFor={inputId}>
        {t('sphere.identity.description')}
      </label>
      <Field name={DESCRIPTION_FIELD}>
        {({ field }: any) => {
          if (isMobile) {
            return (
              <InputTextarea
                id={inputId}
                value={getPlainText(field.value)}
                onChange={(e) => setFieldValue(DESCRIPTION_FIELD, e.target.value)}
                rows={4}
                placeholder={placeholder}
                disabled={!editable}
                className="sphere-description-mobile"
              />
            );
          }
          if (!isEditing) {
            return (
              <InputTextarea
                id={inputId}
                value={getPlainText(field.value)}
                onFocus={() => editable && setIsEditing(true)}
                onChange={() => {
                  /* the preview is read-only until the rich editor opens */
                }}
                rows={6}
                placeholder={placeholder}
                disabled={!editable}
                className="sphere-description-preview"
                style={{ height: EDITOR_HEIGHT }}
              />
            );
          }
          return (
            <TextEditorAtom
              {...field}
              value={field.value}
              onChange={(value: string) => setFieldValue(DESCRIPTION_FIELD, value)}
              height={EDITOR_HEIGHT}
              disabled={!editable}
              ariaLabelledBy={labelId}
              autoFocus
            />
          );
        }}
      </Field>
      <ErrorMessage name={DESCRIPTION_FIELD} component="div" className="invalid-feedback" />
    </div>
  );
};

export default SphereDescriptionEditor;
